import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import useCurrentLocation from "../hooks/useCurrentLocation";
import { Sun, Moon, Warning, MapPin, CaretRight } from "@phosphor-icons/react";
import PanchangDrawer from "./PanchangDrawer";

export default function PanchangBar() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [personalized, setPersonalized] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [locLabel, setLocLabel] = useState("New Delhi");
  const currentLoc = useCurrentLocation();

  useEffect(() => {
    // Prefer the seeker's CURRENT location (browser geolocation) so the
    // panchang reflects where they actually are right now. Birth coords are
    // the secondary fallback (handles the case where the user blocked
    // geolocation but already provided birth details). Final fallback is the
    // server default (New Delhi).
    let params;
    let label = "New Delhi";
    let mode = "default";
    if (currentLoc.status === "ready") {
      params = { lat: currentLoc.lat, lon: currentLoc.lon, timezone: currentLoc.timezone };
      label = currentLoc.city || "your area";
      mode = "current";
    } else {
      const coords = user?.kundali?.coordinates;
      const tz = user?.birth_details?.timezone;
      if (coords && tz) {
        params = { lat: coords.lat, lon: coords.lon, timezone: tz };
        label = (user?.birth_details?.place || "").split(",")[0].trim() || label;
        mode = "birth";
      }
    }
    setPersonalized(mode !== "default");
    setLocLabel(label);
    api.get("/panchang/today", params ? { params } : undefined)
      .then(({ data }) => setData(data))
      .catch(() => {});
  }, [user, currentLoc.status, currentLoc.lat, currentLoc.lon, currentLoc.timezone]);

  // Open the Today's Brief drawer when:
  //   (a) the URL hash is "#brief" on initial mount (handles deep-links / refreshes), OR
  //   (b) the "panchang:open" CustomEvent fires (in-app triggers from hamburger menu)
  // Hash routing alone is unreliable on re-clicks because react-router's Link
  // short-circuits when the destination matches the current URL — so we layer
  // a CustomEvent on top for the in-app menu click path.
  useEffect(() => {
    if (location.hash === "#brief") {
      setDrawerOpen(true);
      window.history.replaceState(null, "", location.pathname + location.search);
    }
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    const onOpen = () => setDrawerOpen(true);
    window.addEventListener("panchang:open", onOpen);
    return () => window.removeEventListener("panchang:open", onOpen);
  }, []);

  // Note: the bar itself only renders once panchang data has loaded, but the
  // drawer is always mounted at the bottom of this component so the
  // `#brief` hash deep-link works even before the bar's data lands.
  const cityLabel = locLabel;

  return (
    <>
      {data && (
      <button
        type="button"
        onClick={() => setDrawerOpen(true)}
        className="w-full text-[#1A1410] hover:brightness-105 transition-[filter] cursor-pointer text-left"
        style={{
          background: "linear-gradient(90deg, #FFE9B5 0%, #FFD18A 50%, #FFBD66 100%)",
          borderBottom: "1px solid rgba(217, 119, 6, 0.18)",
        }}
        data-testid="panchang-bar"
        aria-label="Open today's brief"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2 flex items-center justify-between gap-4 text-xs sm:text-sm">
          <div className="flex items-center gap-4 sm:gap-6 overflow-x-auto whitespace-nowrap" data-testid="panchang-items">
            <span className="label-eyebrow !text-[#7C2D12] !text-[10px] flex items-center gap-1.5">
              <MapPin size={11} weight="fill" /> {cityLabel} · {data.weekday}
            </span>
            <span className="hidden sm:inline opacity-30">|</span>
            <span data-testid="panchang-tithi">
              <span className="opacity-60 mr-1">Tithi</span>
              <span className="font-medium">{data.tithi_full}</span>
            </span>
            <span className="opacity-30">|</span>
            <span data-testid="panchang-nakshatra">
              <span className="opacity-60 mr-1">Nakshatra</span>
              <span className="font-medium">{data.nakshatra}</span>
            </span>
            {data.rahu_kaal && (
              <>
                <span className="opacity-30">|</span>
                <span className="flex items-center gap-1.5 text-[#9A3412] font-medium" data-testid="panchang-rahu">
                  <Warning size={12} weight="fill" /> Rahu Kaal {data.rahu_kaal.from}–{data.rahu_kaal.to}
                </span>
              </>
            )}
          </div>
          <div className="hidden md:flex items-center gap-4 opacity-70 shrink-0">
            {data.sunrise && (
              <span className="flex items-center gap-1.5" title="Sunrise"><Sun size={12} weight="fill" />{data.sunrise}</span>
            )}
            {data.sunset && (
              <span className="flex items-center gap-1.5" title="Sunset"><Moon size={12} weight="fill" />{data.sunset}</span>
            )}
            <span className="flex items-center gap-1 text-[#7C2D12] font-medium border border-[#7C2D12]/20 rounded-full px-2.5 py-0.5">
              Today's brief <CaretRight size={11} weight="bold" />
            </span>
          </div>
        </div>
      </button>
      )}
      <PanchangDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  );
}
